import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom"; // For getting params from URL
import axiosInstance from "../axiosinstance";
import "./ViewInvoice.css";
import API_URL from "../Config"; // Adjust the path based on your file structure
import { format } from "date-fns";
import { Printer } from "lucide-react"; // Import printer icon
import { BiArrowBack } from "react-icons/bi";
// import LogoGajjarGroup from '../../public/assests/logo/gajjar_group.png'


export default function TaxInvoice({ isSidebarExpanded }) {
  const { invoice_id } = useParams(); // Get the ID from the URL
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState(null);
  const [qrcodeUrl, setQrCodeUrl] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const printRef = useRef(null); // NEW: for robust printing

  // Format text to title case (first letter of each word capitalized)
  const formatName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format text to uppercase
  const formatToUppercase = (text, fallback = '') => {
    return text ? text.toString().toUpperCase() : fallback;
  };

  // Capitalize first letter of a string
  const capitalizeFirstLetter = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  // Indian Currency Formatter with ₹ symbol
  const formatINR = (value) => {
    if (value === null || value === undefined) return "₹0.00";
    const num = parseFloat(value) || 0;

    return num.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    });
  };

  // ⬅️ MOVE BOTH FUNCTIONS HERE (INSIDE COMPONENT!)
  const getDiscountLabel = () => {
    if (!invoiceData?.overall_discount || invoiceData.overall_discount === 0) {
      return "Overall Discount";
    }

    if (invoiceData.discount_type?.toLowerCase() === "percentage") {
      return "Overall Discount (%)";
    }

    return "Overall Discount (₹)";
  };

  const getDiscountValue = () => {
    if (!invoiceData?.overall_discount || invoiceData.overall_discount === 0) {
      return formatINR(0);
    }

    if (invoiceData.discount_type?.toLowerCase() === "percentage") {
      return `${invoiceData.overall_discount} %`;
    }

    return formatINR(invoiceData.overall_discount);
  };


  const toFileUrl = (p) => {
    if (!p) return null;
    const cleaned = String(p)
      .replace(/\\/g, "/") // windows backslashes -> forward slashes
      .replace(/^\.?\/*/, ""); // remove leading "./" or "/" if present

    // If already absolute, return as-is
    if (/^https?:\/\//i.test(cleaned)) return cleaned;

    // Otherwise, prefix API_URL
    return `${API_URL.replace(/\/+$/, "")}/${cleaned}`;
  };

  // Fetch invoice data based on the ID
  const fetchInvoiceData = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/view_invoice/${invoice_id}`
      );
      
      setInvoiceData(response.data.data[0]);
      console.log("Raw Invoice Data:", response.data.data[0]); // Debug log to inspect data structure
      const inv = response.data.data[0];
      setInvoiceData(inv);
      setLogoUrl(toFileUrl(inv?.logo || null));
      let qrPath = toFileUrl(inv?.subsidiary_qr || null);
      setQrCodeUrl(qrPath);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchInvoiceData();
  }, [invoice_id]);

  // Print only the invoice content in a clean window, with strong page-break rules
  const handlePrint = () => {
    if (!printRef.current) return;

    const invoiceHtml = printRef.current.innerHTML;
    const styles = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]')
    )
      .map((n) => n.outerHTML)
      .join("\n");

    const w = window.open("", "", "width=900,height=650");
    if (!w) return;

    w.document.open();
    w.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Invoice ${invoiceData?.invoice_number ?? ""}</title>
          ${styles}
          <style>
            @page { size: A4; margin: 10mm; }
            html, body { height: auto; }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              font-family: Arial, sans-serif;
            }
            /* Ensure wrappers can expand; no clipping */
            .invoice-container-form, .invoice-container, .view-invoice-container {
              max-height: none !important;
              height: auto !important;
              overflow: visible !important;
            }
            /* Tables: allow table to split, keep rows intact, repeat headers */
            table { page-break-inside: auto !important; break-inside: auto !important; width: 100%; border-collapse: collapse; }
            thead { display: table-header-group !important; }
            tfoot { display: table-footer-group !important; }
            tr { page-break-inside: avoid !important; break-inside: avoid !important; }

            /* Keep totals + words + footer + signature together */
            .summary-block {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              page-break-before: auto !important;
            }

            /* Never force footer to next page */
            .view-footer { page-break-before: auto !important; }
          </style>
        </head>
        <body>
          ${invoiceHtml}
          <script>
            window.onload = function () {
              window.focus();
              window.print();
              setTimeout(function(){ window.close(); }, 200);
            };
          <\/script>
        </body>
      </html>
    `);
    w.document.close();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!invoiceData) {
    return <div>No invoice data found</div>;
  }

  console.log("Invoice Data:", invoiceData);

  return (
    <div className="invoice-container-form">
      <button onClick={() => navigate(-1)} className="view-invoice-back-btn">
        <span className="view-back-btn">
          <BiArrowBack />
          Back
        </span>
      </button>
      {/* PRINTABLE AREA */}
      <div className="view-table-container-content" ref={printRef}>
        <div className="view-invoice-container">
          <div className="company-logo">
            <img
              // src={logoUrl || "/assests/logo/gajjar_group.png"} 
              src={logoUrl || "/assests/logo/gajjar_group.png"}
              alt="Company Logo"
              style={{ maxHeight: "60px", objectFit: "contain" }}
              onError={(e) => {
                // hide the broken image and keep layout clean
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="view-invoice-title">Tax Invoice</h1>
          </div>

          <div className="view-header">
            <div className="view-header-left">
              <h2 className="view-company-name">
                {formatName(invoiceData["Subsidiary_Name"]) || '-'}
              </h2>
              <p className="view-address view-invoice-address">
                {formatName(invoiceData["Subsidiary_Address"]) || "-"}
              </p>
            </div>

          </div>



          <div className="view-invoice-details-container">
            <div className="view-invoice-details-row">
              <div className="view-invoice-details-label">Invoice Number</div>
              <div className="view-invoice-details-value">
                : {invoiceData.invoice_number || "N/A"}
              </div>
            </div>
            <div className="view-invoice-details-row">
              <div className="view-invoice-details-label">Invoice Date</div>
              <div className="view-invoice-details-value">
                :{" "}
                {invoiceData.invoice_date
                  ? format(new Date(invoiceData.invoice_date), "dd-MM-yyyy")
                  : "N/A"}
              </div>
            </div>
            <div className="view-invoice-details-row">
              <div className="view-invoice-details-label">Due Date</div>
              <div className="view-invoice-details-value">
                :{" "}
                {invoiceData.due_date
                  ? format(new Date(invoiceData.due_date), "dd-MM-yyyy")
                  : "N/A"}
              </div>
            </div>
          </div>

          <table className="view-billing-shipping-table">
            <thead>
              <tr>
                <th className="view-billing-header">Bill To</th>
                <th className="view-shipping-header">Ship To</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="view-billing-data">
                  <span className="client-name-custom">
                    {formatName(invoiceData.client_name) || "Client name not provided"}
                  </span>
                  <div className="view-address ">
                    <p>{formatName(invoiceData.Landmark) || "Landmark not provided"}</p>
                    <p>
                      {formatName(invoiceData.Office_number) ||
                        "Office number not provided"}
                    </p>
                    <p>{formatName(invoiceData.area) || "Area not provided"}</p>
                    <p>
                      {invoiceData.postal_code || "Postal code not provided"}
                    </p>
                  </div>
                </td>

                <td className="view-shipping-data">
                  <span>Same as Billing Address</span>
                </td>
              </tr>
            </tbody>
          </table>

          {/* Items Table */}
          <table className="view-items-table">
            <thead>
              <tr>
                <th className="view-item-no">#</th>
                <th className="view-item-description">Item & Description</th>
                <th className="view-item-qty">Qty</th>
                <th className="view-item-rate">Rate</th>
                <th className="view-item-gst">GST %</th>

                <th className="view-item-total">Amount</th>
              </tr>
            </thead>
            <tbody className="view-invoice-text">
              {invoiceData.informatiion.map((item, index) => (
                <tr key={index}>
                  <td className="view-item-no">{index + 1}</td>
                  <td className="view-item-description">{item.service_name}</td>
                  <td className="view-item-qty">{item.quantity}</td>
                  <td className="view-item-rate">{formatINR(item.rate)}</td>
                  <td className="view-item-gst">{item.tax}%</td>
                  <td className="view-item-total">{formatINR(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals + Words + Footer + Signature kept together */}
          <section className="summary-block">
            {/* Totals Table */}
            <table className="view-totals-table">
              <tbody>
                <tr>
                  <td colSpan="7" className="view-totals-label">
                    Sub Total
                  </td>
                  <td colSpan="2" className="view-totals-amount">
                    {formatINR(invoiceData.sub_total)}
                  </td>
                </tr>
                <tr>
                  <td colSpan="7" className="view-totals-label">
                    {getDiscountLabel()}
                  </td>
                  <td colSpan="2" className="view-totals-amount">
                    {getDiscountValue()}
                  </td>

                </tr>
                <tr>
                  <td colSpan="7" className="view-totals-label">
                    <strong>Total</strong>
                  </td>
                  <td colSpan="2" className="view-totals-amount">
                    <strong>{formatINR(invoiceData.total_amount)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="view-total-in-words">
              Total In Words: <strong>{capitalizeFirstLetter(invoiceData.amount_in_words)}</strong>
            </p>

            <div className="view-footer">
              <p className="view-account-details">
                Account Details:
                <br />
                Account Holder: {formatName(invoiceData.account_holder) || "Not available"}
                <br />
                Account Number: {invoiceData.Account_number || "Not available"}
                <br />
                IFSC: {invoiceData.ifsc_code || "Not available"}
                <br />
                Branch: {formatToUppercase(invoiceData.branch) || "Not available"}
                <br />
                PAN No.: {formatToUppercase(invoiceData.pan_no) || "Not available"}
                <br />
                UPI ID: {invoiceData.vpa || "Not available"}
              </p>
 <div className="company-logo">
            <img
              // src={logoUrl || "/assests/logo/gajjar_group.png"} 
              src={qrcodeUrl}
              alt="Company Logo"
              style={{ maxHeight: "60px", objectFit: "contain", marginTop: "10px" }}
              onError={(e) => {
                // hide the broken image and keep layout clean
                e.currentTarget.style.display = "none";
              }}
            />
           
          </div>
            </div>

            <div className="view-signature">
              <p className="view-signature-text">Authorized Signature</p>
            </div>
          </section>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "20px", position: "absolute", top: 0, right: 0 }}>
        <button
          onClick={handlePrint}
          className="print-button"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            padding: "8px 18px",
            backgroundColor: "#f8f9fa",
            color: "#333",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            marginLeft: "auto",
          }}
        >
          <Printer size={18} />
          Print Invoice
        </button>
      </div>
    </div >
  );
}
